import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { assetImage, formatIDR } from "@/lib/api";
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export function CartDrawer() {
  const { items, removeItem, setLicense, subtotal, open, setOpen, count } = useCart();
  const { t, localizedTitle } = useI18n();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col sm:max-w-md p-0" data-testid="cart-drawer">
        <SheetHeader className="border-b border-slate-200 px-6 py-4">
          <SheetTitle className="flex items-center gap-2 font-display">
            <ShoppingBag size={18} /> {t("cart.title")}
            <span className="text-sm font-normal text-slate-400">
              ({count} {count === 1 ? t("cart.item") : t("cart.items")})
            </span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
            <ShoppingBag size={48} className="text-slate-200" />
            <p className="mt-4 font-semibold text-slate-900">{t("cart.empty")}</p>
            <p className="mt-1 text-sm text-slate-500">{t("cart.emptyDesc")}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3" data-testid={`cart-item-${item.id}`}>
                  <img
                    src={assetImage(item)}
                    alt=""
                    className="h-16 w-16 shrink-0 rounded-lg object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="line-clamp-1 text-sm font-semibold text-slate-900">
                        {localizedTitle(item)}
                      </h4>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-slate-400 hover:text-red-500"
                        data-testid={`cart-remove-${item.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1">
                      {["standard", "commercial"].map((lic) => (
                        <button
                          key={lic}
                          onClick={() => setLicense(item.id, lic)}
                          className={`rounded px-2 py-0.5 text-[10px] font-medium capitalize transition ${
                            item.license === lic
                              ? "bg-slate-900 text-white"
                              : "bg-slate-100 text-slate-600"
                          }`}
                          data-testid={`cart-license-${item.id}-${lic}`}
                        >
                          {t(`detail.${lic}`)}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1.5 font-mono text-sm font-bold text-slate-900">
                      {formatIDR(item.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 px-6 py-5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{t("cart.subtotal")}</span>
                <span className="font-mono font-bold text-slate-900">{formatIDR(subtotal)}</span>
              </div>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/checkout");
                }}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 text-sm font-semibold text-white hover:bg-blue-700 transition"
                data-testid="cart-checkout-btn"
              >
                {t("cart.checkout")} <ArrowRight size={16} />
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
